import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { email } from "../services/api";
import { useApp } from "../context/AppContext";
import { formatDate } from "../utils/dateUtils";
import {
  BackIcon,
  RefreshIcon,
  CheckIcon,
  CloseIcon,
  ProductIcon,
} from "../components/Icons";

function EmailScanning() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshCounts } = useApp();
  const [status, setStatus] = useState(null);
  const [pendingProducts, setPendingProducts] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState(null);
  const [dismissedItems, setDismissedItems] = useState([]);
  const [historyPeriod, setHistoryPeriod] = useState("30d");
  const isMountedRef = useRef(true);
  const pollTimeoutRef = useRef(null);

  useEffect(() => {
    isMountedRef.current = true;

    const handleOAuthCallback = async () => {
      const code = searchParams.get("code");
      if (code) {
        // Exchange code for tokens
        try {
          setLoading(true);
          await email.callback(code);
          // Clear the URL params
          window.history.replaceState({}, "", "/email-scanning");
          loadStatus(true); // Auto-sync after connecting
        } catch (err) {
          console.error("OAuth callback failed:", err);
          setError("Failed to connect Gmail. Please try again.");
          setLoading(false);
        }
        return;
      }

      // Check for connection status from callback
      if (searchParams.get("connected") === "true") {
        loadStatus(false); // Don't auto-sync, let user control
      } else if (searchParams.get("error")) {
        setError("Failed to connect Gmail. Please try again.");
        loadStatus(false);
      } else {
        // Normal page load - don't auto-sync, let user control
        loadStatus(false);
      }
    };

    handleOAuthCallback();

    // Cleanup on unmount
    return () => {
      isMountedRef.current = false;
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
      }
    };
  }, [searchParams]);

  const loadStatus = async (autoSyncIfStale = false) => {
    try {
      setLoading(true);
      const { data } = await email.getStatus();
      setStatus(data);

      if (data.connected) {
        await loadPendingProducts();

        // If a scan is in progress, start polling
        if (data.scanStatus === "scanning") {
          setScanning(true);
          startPolling();
        } else if (autoSyncIfStale) {
          // Auto-sync if last scan was more than 24 hours ago
          const lastScan = data.lastScanAt ? new Date(data.lastScanAt) : null;
          const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);

          if (!lastScan || lastScan < oneDayAgo) {
            console.log("[EmailScan] Auto-syncing (last scan > 24 hours ago)");
            handleScan();
          }
        }
      }
    } catch (err) {
      console.error("Failed to load status:", err);
      setError("Failed to load email status");
    } finally {
      setLoading(false);
    }
  };

  const startPolling = () => {
    let attempts = 0;
    const maxAttempts = 5; // Give up after 5 attempts if never see scanning status

    const pollProgress = async () => {
      // Stop polling if component unmounted
      if (!isMountedRef.current) {
        console.log("[EmailScan] Component unmounted, stopping poll");
        return;
      }

      try {
        const { data } = await email.getStatus();

        // Check again after async call
        if (!isMountedRef.current) return;

        console.log(
          "[EmailScan] Status:",
          data.scanStatus,
          data.scanProgress,
          "/",
          data.scanTotal,
        );
        setStatus(data);

        if (data.scanStatus === "scanning") {
          attempts = 0; // Reset attempts when we see scanning
          pollTimeoutRef.current = setTimeout(pollProgress, 1000);
        } else if (attempts < maxAttempts) {
          // Haven't seen scanning status yet, keep trying
          attempts++;
          pollTimeoutRef.current = setTimeout(pollProgress, 500);
        } else {
          // Scan complete or never started
          console.log("[EmailScan] Polling stopped");
          setScanning(false);
          await loadPendingProducts();
        }
      } catch (err) {
        console.error("[EmailScan] Poll error:", err);
        if (isMountedRef.current) {
          setScanning(false);
        }
      }
    };

    pollProgress();
  };

  const loadPendingProducts = async () => {
    try {
      const { data } = await email.getPending();
      setPendingProducts(data);
    } catch (err) {
      console.error("Failed to load pending products:", err);
    }
  };

  const handleConnect = async () => {
    try {
      const { data } = await email.getAuthUrl();
      window.location.href = data.url;
    } catch (err) {
      setError("Failed to get authorization URL");
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm("Disconnect Gmail? Pending items will be kept."))
      return;

    try {
      await email.disconnect();
      setStatus({ connected: false });
      setPendingProducts([]);
    } catch (err) {
      setError("Failed to disconnect");
    }
  };

  const handleScan = async () => {
    try {
      setScanning(true);
      console.log("[EmailScan] Starting scan...");
      // Poll /email/status while the batches run so the progress bar shows.
      startPolling();

      let keepScanning = true;
      let totalScanned = 0;
      let totalProducts = 0;

      while (keepScanning) {
        const scanResult = await email.scan(historyPeriod);
        const { scannedEmails, newProducts, partial, remaining, warning } =
          scanResult.data;

        totalScanned += scannedEmails || 0;
        totalProducts += newProducts || 0;

        console.log(
          `[EmailScan] Batch complete: ${scannedEmails} emails, ${newProducts} products. Partial: ${partial}, Remaining: ${remaining}`,
        );

        if (warning) {
          // The worker aborted mid-scan (partial: true, remaining: -1).
          // Surface it instead of pretending the mailbox finished.
          console.warn("[EmailScan]", warning);
          setError(warning);
          keepScanning = false;
        } else if (partial && remaining > 0) {
          // More emails to process, continue scanning
          console.log(
            `[EmailScan] Continuing scan, ${remaining} emails remaining...`,
          );
          await loadPendingProducts(); // Refresh pending list between batches
        } else {
          keepScanning = false;
        }
      }

      console.log(
        `[EmailScan] Complete! Total: ${totalScanned} emails, ${totalProducts} products`,
      );
      await loadPendingProducts();
      await loadStatus();
      setScanning(false);
    } catch (err) {
      console.error("[EmailScan] Scan error:", err);
      setError("Failed to scan emails");
      setScanning(false);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const selectAll = () => {
    if (selectedIds.size === pendingProducts.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(pendingProducts.map((p) => p.id)));
    }
  };

  const handleAddSelected = async () => {
    if (selectedIds.size === 0) return;

    try {
      setAdding(true);
      await email.addProducts(Array.from(selectedIds));
      setSelectedIds(new Set());
      await loadPendingProducts();
      await loadStatus();
      refreshCounts();
    } catch (err) {
      setError("Failed to add products");
    } finally {
      setAdding(false);
    }
  };

  const handleDismiss = async (ids) => {
    const idsArray = Array.isArray(ids) ? ids : [ids];

    // Store dismissed items for potential undo
    const itemsToStore = pendingProducts.filter((p) => idsArray.includes(p.id));

    try {
      await email.dismissProducts(idsArray);

      // Drop dismissed rows from the selection so "Add N Items" can't act on
      // soft-deleted drafts that are no longer on screen.
      setSelectedIds((prev) => {
        const next = new Set(prev);
        idsArray.forEach((id) => next.delete(id));
        return next;
      });

      // Add to dismissed list for undo
      setDismissedItems((prev) => [...prev, ...itemsToStore]);

      await loadPendingProducts();
      await loadStatus();
    } catch (err) {
      setError("Failed to dismiss");
    }
  };

  const handleUndoOne = async (item) => {
    try {
      await email.restoreProduct(item.id);

      // Remove from dismissed list
      setDismissedItems((prev) => prev.filter((i) => i.id !== item.id));

      await loadPendingProducts();
      await loadStatus();
    } catch (err) {
      setError("Failed to undo");
    }
  };

  const handleUndoAll = async () => {
    if (dismissedItems.length === 0) return;

    try {
      await Promise.all(
        dismissedItems.map((item) => email.restoreProduct(item.id)),
      );

      setDismissedItems([]);

      await loadPendingProducts();
      await loadStatus();
    } catch (err) {
      setError("Failed to undo");
    }
  };

  const formatPrice = (price) => {
    if (price === null || price === undefined) return "";
    return `$${parseFloat(price).toFixed(2)}`;
  };

  const formatTimeAgo = (dateStr) => {
    if (!dateStr) return "Never";
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60)
      return `${diffMins} minute${diffMins > 1 ? "s" : ""} ago`;

    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24)
      return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;

    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
  };

  if (loading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", paddingBottom: "80px" }}>
      <div className="header">
        <div className="header-content">
          <button
            onClick={() => navigate("/")}
            className="btn btn-ghost btn-icon"
          >
            <BackIcon size={20} />
          </button>
          <div className="header-text">
            <h1 className="header-title">Email Scanning</h1>
            <p className="header-subtitle">
              Auto-add items from purchase emails
            </p>
          </div>
          {status?.connected && (
            <div
              className="header-actions-right"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "var(--spacing-sm)",
              }}
            >
              <select
                value={historyPeriod}
                onChange={(e) => setHistoryPeriod(e.target.value)}
                disabled={scanning}
                style={{
                  padding: "4px 8px",
                  borderRadius: "var(--radius-sm)",
                  border: "1px solid var(--border-color)",
                  background: "var(--bg-secondary)",
                  color: "var(--text-primary)",
                  fontSize: "0.875rem",
                }}
              >
                <option value="30d">Last 30 days</option>
                <option value="90d">Last 90 days</option>
                <option value="1y">Last year</option>
                <option value="all">All time</option>
              </select>
              <button
                onClick={handleScan}
                disabled={scanning}
                className="btn btn-ghost btn-icon"
                title="Scan for new emails"
              >
                <RefreshIcon size={20} className={scanning ? "spin" : ""} />
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="container">
        {error && (
          <div
            className="card"
            style={{
              marginBottom: "var(--spacing-lg)",
              padding: "var(--spacing-md)",
              background: "rgba(239, 68, 68, 0.1)",
              borderColor: "var(--danger)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <span style={{ color: "var(--danger)" }}>{error}</span>
              <button
                onClick={() => setError(null)}
                className="btn btn-ghost btn-icon"
              >
                <CloseIcon size={16} />
              </button>
            </div>
          </div>
        )}

        {/* Connection Status Card */}
        <div className="card" style={{ marginBottom: "var(--spacing-lg)" }}>
          {status?.connected ? (
            <div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "var(--spacing-md)",
                  marginBottom: "var(--spacing-md)",
                }}
              >
                <div
                  style={{
                    width: "40px",
                    height: "40px",
                    borderRadius: "50%",
                    background: "rgba(34, 197, 94, 0.1)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "var(--success)",
                  }}
                >
                  <CheckIcon size={20} />
                </div>
                <div>
                  <div style={{ fontWeight: 600 }}>Connected</div>
                  <div className="text-muted" style={{ fontSize: "0.875rem" }}>
                    {status.email}
                  </div>
                </div>
              </div>
              {status.scanStatus === "scanning" ? (
                <div style={{ marginTop: "var(--spacing-sm)" }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginBottom: "var(--spacing-xs)",
                    }}
                  >
                    <span style={{ fontSize: "0.875rem", fontWeight: 500 }}>
                      Scanning emails...
                    </span>
                    <span
                      className="text-muted"
                      style={{ fontSize: "0.875rem" }}
                    >
                      {status.scanProgress} / {status.scanTotal}
                    </span>
                  </div>
                  <div
                    style={{
                      height: "6px",
                      background: "var(--bg-secondary)",
                      borderRadius: "3px",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        height: "100%",
                        width: `${status.scanTotal > 0 ? (status.scanProgress / status.scanTotal) * 100 : 0}%`,
                        background: "var(--accent-primary)",
                        borderRadius: "3px",
                        transition: "width 0.3s ease",
                      }}
                    />
                  </div>
                </div>
              ) : (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <span className="text-muted" style={{ fontSize: "0.875rem" }}>
                    Last synced: {formatTimeAgo(status.lastScanAt)}
                  </span>
                  <button
                    onClick={handleDisconnect}
                    className="btn btn-ghost btn-sm"
                    style={{ color: "var(--danger)" }}
                  >
                    Disconnect
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div style={{ textAlign: "center", padding: "var(--spacing-lg)" }}>
              <div style={{ marginBottom: "var(--spacing-md)" }}>
                <svg
                  width="48"
                  height="48"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  style={{ color: "var(--text-muted)" }}
                >
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <path d="M22 6l-10 7L2 6" />
                </svg>
              </div>
              <h3 style={{ marginBottom: "var(--spacing-sm)" }}>
                Connect Gmail
              </h3>
              <p
                className="text-muted"
                style={{ marginBottom: "var(--spacing-lg)" }}
              >
                Connect your Gmail account to automatically scan for purchase
                emails and add items to your inventory.
              </p>
              <button onClick={handleConnect} className="btn btn-primary">
                Connect Gmail
              </button>
            </div>
          )}
        </div>

        {/* Pending Products */}
        {status?.connected && (
          <>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "var(--spacing-md)",
              }}
            >
              <h2 style={{ fontSize: "1.25rem", fontWeight: 600 }}>
                Pending Items ({pendingProducts.length})
              </h2>
              {pendingProducts.length > 0 && (
                <button onClick={selectAll} className="btn btn-ghost btn-sm">
                  {selectedIds.size === pendingProducts.length
                    ? "Deselect All"
                    : "Select All"}
                </button>
              )}
            </div>

            {pendingProducts.length === 0 ? (
              <div
                className="card text-center"
                style={{ padding: "var(--spacing-2xl)" }}
              >
                <div
                  style={{
                    marginBottom: "var(--spacing-md)",
                    color: "var(--text-muted)",
                  }}
                >
                  <CheckIcon size={48} />
                </div>
                <h3 style={{ marginBottom: "var(--spacing-sm)" }}>
                  All caught up!
                </h3>
                <p className="text-muted">
                  No pending items from emails. Click the sync button to scan
                  for new purchase emails.
                </p>
              </div>
            ) : (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "var(--spacing-sm)",
                }}
              >
                {pendingProducts.map((product) => (
                  <div
                    key={product.id}
                    className="card"
                    style={{
                      padding: "var(--spacing-md)",
                      background: selectedIds.has(product.id)
                        ? "rgba(59, 130, 246, 0.05)"
                        : undefined,
                      borderColor: selectedIds.has(product.id)
                        ? "var(--accent-primary)"
                        : undefined,
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "var(--spacing-md)",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedIds.has(product.id)}
                      onChange={() => toggleSelect(product.id)}
                      style={{
                        marginTop: "4px",
                        cursor: "pointer",
                        flexShrink: 0,
                      }}
                    />
                    {product.image_path ? (
                      <img
                        src={product.image_path}
                        alt={product.name}
                        style={{
                          width: "50px",
                          height: "50px",
                          minWidth: "50px",
                          objectFit: "cover",
                          borderRadius: "var(--radius-sm)",
                          flexShrink: 0,
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          width: "50px",
                          height: "50px",
                          minWidth: "50px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          background: "var(--bg-secondary)",
                          borderRadius: "var(--radius-sm)",
                          color: "var(--text-muted)",
                          flexShrink: 0,
                        }}
                      >
                        <ProductIcon size={24} />
                      </div>
                    )}
                    <div style={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
                      <div
                        style={{
                          fontWeight: 500,
                          marginBottom: "4px",
                          wordBreak: "break-word",
                        }}
                      >
                        {product.name}
                      </div>
                      <div
                        style={{
                          display: "flex",
                          gap: "var(--spacing-md)",
                          flexWrap: "wrap",
                          fontSize: "0.875rem",
                        }}
                      >
                        {product.purchase_price && (
                          <span
                            style={{
                              color: "var(--accent-primary)",
                              fontWeight: 600,
                            }}
                          >
                            {formatPrice(product.purchase_price)}
                          </span>
                        )}
                        {product.quantity > 1 && (
                          <span className="text-muted">
                            Qty: {product.quantity}
                          </span>
                        )}
                        {product.purchase_date && (
                          <span className="text-muted">
                            Purchased: {formatDate(product.purchase_date)}
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => handleDismiss(product.id)}
                      className="btn btn-ghost btn-icon btn-sm"
                      title="Dismiss"
                      style={{ flexShrink: 0 }}
                    >
                      <CloseIcon size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Undo bar */}
            {dismissedItems.length > 0 && (
              <div
                style={{
                  padding: "var(--spacing-sm) var(--spacing-md)",
                  background: "var(--bg-tertiary)",
                  borderRadius: "var(--radius-md)",
                  marginTop: "var(--spacing-sm)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom:
                      dismissedItems.length > 1 ? "var(--spacing-xs)" : 0,
                  }}
                >
                  <span style={{ fontSize: "0.875rem" }}>
                    {dismissedItems.length === 1
                      ? `Dismissed "${dismissedItems[0].name}"`
                      : `Dismissed ${dismissedItems.length} items`}
                  </span>
                  {dismissedItems.length === 1 ? (
                    <button
                      onClick={() => handleUndoOne(dismissedItems[0])}
                      className="btn btn-ghost"
                      style={{
                        padding: "4px 8px",
                        fontSize: "0.875rem",
                        color: "var(--accent-primary)",
                      }}
                    >
                      Undo
                    </button>
                  ) : (
                    <button
                      onClick={handleUndoAll}
                      className="btn btn-ghost"
                      style={{
                        padding: "4px 8px",
                        fontSize: "0.875rem",
                        color: "var(--accent-primary)",
                      }}
                    >
                      Undo All
                    </button>
                  )}
                </div>
                {dismissedItems.length > 1 && (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "4px",
                    }}
                  >
                    {dismissedItems.map((item) => (
                      <div
                        key={item.id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          fontSize: "0.8rem",
                          color: "var(--text-muted)",
                        }}
                      >
                        <span
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            marginRight: "var(--spacing-sm)",
                          }}
                        >
                          {item.name}
                        </span>
                        <button
                          onClick={() => handleUndoOne(item)}
                          style={{
                            background: "none",
                            border: "none",
                            padding: "2px 6px",
                            fontSize: "0.8rem",
                            color: "var(--accent-primary)",
                            cursor: "pointer",
                            flexShrink: 0,
                          }}
                        >
                          Undo
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Add Selected Bar */}
      {selectedIds.size > 0 && (
        <div
          style={{
            position: "fixed",
            bottom: "80px",
            left: 0,
            right: 0,
            padding: "var(--spacing-md)",
            background: "var(--bg-card)",
            borderTop: "1px solid var(--border-color)",
            boxShadow: "var(--shadow-lg)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            zIndex: 100,
          }}
        >
          <span style={{ fontWeight: 600 }}>{selectedIds.size} selected</span>
          <div style={{ display: "flex", gap: "var(--spacing-sm)" }}>
            <button
              onClick={() => handleDismiss(Array.from(selectedIds))}
              className="btn btn-secondary"
            >
              Dismiss
            </button>
            <button
              onClick={handleAddSelected}
              disabled={adding}
              className="btn btn-primary"
            >
              {adding
                ? "Adding..."
                : `Add ${selectedIds.size} Item${selectedIds.size > 1 ? "s" : ""}`}
            </button>
          </div>
        </div>
      )}

      <style>{`
        .spin {
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export default EmailScanning;
